import { findAndSetColumnSortOrder, ProgressBarWithValueCell } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import type { dateForTooltip } from "app/services/DurationService";

import type { IIndustryPublisherRow } from "../overview/industry/top-publishers/useIndustryTopPublishersTable";
import { formatPercentValue } from "../overview/utils/utils";
import { IndustryPathCell } from "../utils/IndustryPathCell";
import type { ISortProps } from "../utils/utils";
import { NA_VALUE, getChangeCellValues } from "../utils/utils";
import { renderWebsiteTooltipCell } from "../utils/websiteTooltipCell";

const INDEX_COLUMN_MIN_WIDTH = 60;
const SHARE_VALUE_MIN_WIDTH = "48px";

const renderChangeCell = (value: number, precision: number, changeState?: string) => (
    <ChangeStateCell
        value={{ change: value, changeState }}
        undefinedSign={NA_VALUE}
        isPercentage={true}
        cellFilter={`simplePercentage:${precision}`}
    />
);

export const getColumns = (
    totalRecords: number,
    currentPage: number,
    pageSize: number,
    durationTooltipParams: dateForTooltip,
    isWindow: boolean,
    sort: ISortProps,
    onPublisherClick: (publisher: string) => void,
): ColumnsType<IIndustryPublisherRow> =>
    (
        [
            {
                ...getIndexCellPropsForTableV2<IIndustryPublisherRow>(
                    currentPage,
                    pageSize,
                    undefined,
                    INDEX_COLUMN_MIN_WIDTH,
                ),
            },
            {
                title: translate("ad.intelligence.advertiser.publishers.publishers.column.title", {
                    totalRecords: numberFilter()(totalRecords),
                }),
                tooltipTitle: translate(
                    "ad.intelligence.display.advertiser.publishers.publishers.column.tooltip.title",
                ),
                tooltipSubtitle: translate("ad.intelligence.display.advertiser.publishers.publishers.column.tooltip"),
                dataIndex: "publisher",
                width: 200,
                fixed: "left",
                render: (publisher: string) =>
                    renderWebsiteTooltipCell(publisher, {
                        onDomainClick: onPublisherClick,
                        dataAutomation: "industry-publishers-publisher-link",
                    }),
            },
            {
                title: translate("ad.intelligence.industry.publishers.category.column.title"),
                dataIndex: "category",
                width: 180,
                render: (category: string, row: IIndustryPublisherRow) => (
                    <IndustryPathCell
                        category={category}
                        subCategory={row.subCategory}
                    />
                ),
            },
            {
                title: translate("ad.intelligence.advertiser.publishers.spend.column.title"),
                align: "center",
                key: "spendGroup",
                children: [
                    {
                        title: translate("ad.intelligence.advertiser.publishers.spend.share.column.title"),
                        tooltipTitle: translate(
                            "ad.intelligence.display.advertiser.publishers.spend.share.column.tooltip.title",
                        ),
                        tooltipSubtitle: translate(
                            "ad.intelligence.display.advertiser.publishers.spend.share.column.tooltip",
                        ),
                        dataIndex: "spendShare",
                        width: 172,
                        sorter: true,
                        render: (spendShare: number) => (
                            <ProgressBarWithValueCell
                                share={spendShare}
                                value={formatPercentValue(spendShare)}
                                color={colorsPalettes.blue[400]}
                                valueMinWidth={SHARE_VALUE_MIN_WIDTH}
                            />
                        ),
                    },
                    {
                        title: translate("ad.intelligence.advertiser.publishers.spend.change.column.title"),
                        tooltipTitle: translate(
                            "ad.intelligence.display.advertiser.publishers.spend.change.column.tooltip.title",
                        ),
                        tooltipSubtitle: isWindow
                            ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                            : translate(
                                  "ad.intelligence.display.advertiser.publishers.spend.change.column.tooltip",
                                  durationTooltipParams,
                              ),
                        dataIndex: "spendChange",
                        width: 80,
                        align: "right",
                        render: (spendChange: number, row: IIndustryPublisherRow) => {
                            const { changeState, precision } = getChangeCellValues({
                                value: spendChange,
                                isNew: row.isSpendNew,
                            });
                            return renderChangeCell(spendChange, precision, changeState);
                        },
                    },
                ],
            },
            {
                title: translate("ad.intelligence.advertiser.publishers.impressions.column.title"),
                align: "center",
                key: "impressionsGroup",
                children: [
                    {
                        title: translate("ad.intelligence.advertiser.publishers.impressions.share.column.title"),
                        tooltipTitle: translate(
                            "ad.intelligence.display.advertiser.publishers.impressions.share.column.tooltip.title",
                        ),
                        tooltipSubtitle: translate(
                            "ad.intelligence.display.advertiser.publishers.impressions.share.column.tooltip",
                        ),
                        dataIndex: "impressionsShare",
                        width: 172,
                        sorter: true,
                        render: (impressionsShare: number) => (
                            <ProgressBarWithValueCell
                                share={impressionsShare}
                                value={formatPercentValue(impressionsShare)}
                                color={colorsPalettes.blue[400]}
                                valueMinWidth={SHARE_VALUE_MIN_WIDTH}
                            />
                        ),
                    },
                    {
                        title: translate("ad.intelligence.advertiser.publishers.impressions.change.column.title"),
                        tooltipTitle: translate(
                            "ad.intelligence.display.advertiser.publishers.impressions.change.column.tooltip.title",
                        ),
                        tooltipSubtitle: isWindow
                            ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                            : translate(
                                  "ad.intelligence.display.advertiser.publishers.impressions.change.column.tooltip",
                                  durationTooltipParams,
                              ),
                        dataIndex: "impressionsChange",
                        width: 80,
                        align: "right",
                        render: (impressionsChange: number, row: IIndustryPublisherRow) => {
                            const { changeState, precision } = getChangeCellValues({
                                value: impressionsChange,
                                isNew: row.isImpressionNew,
                            });
                            return renderChangeCell(impressionsChange, precision, changeState);
                        },
                    },
                ],
            },
            {
                title: translate("ad.intelligence.advertiser.publishers.visits.column.title"),
                align: "center",
                key: "visitsGroup",
                children: [
                    {
                        title: translate("ad.intelligence.advertiser.publishers.visits.share.column.title"),
                        tooltipTitle: translate(
                            "ad.intelligence.advertiser.publishers.visits.share.column.tooltipTitle",
                        ),
                        tooltipSubtitle: translate(
                            "ad.intelligence.advertiser.publishers.visits.share.column.tooltipSubtitle",
                        ),
                        dataIndex: "visitsShare",
                        width: 172,
                        sorter: true,
                        render: (visitsShare: number) => (
                            <ProgressBarWithValueCell
                                share={visitsShare}
                                value={formatPercentValue(visitsShare)}
                                color={colorsPalettes.blue[400]}
                                valueMinWidth={SHARE_VALUE_MIN_WIDTH}
                            />
                        ),
                    },
                    {
                        title: translate("ad.intelligence.advertiser.publishers.visits.change.column.title"),
                        tooltipTitle: translate(
                            "ad.intelligence.advertiser.publishers.visits.change.column.tooltipTitle",
                        ),
                        tooltipSubtitle: isWindow
                            ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                            : translate(
                                  "ad.intelligence.advertiser.publishers.visits.change.column.tooltipSubtitle",
                                  durationTooltipParams,
                              ),
                        dataIndex: "visitsChange",
                        width: 80,
                        align: "right",
                        render: (visitsChange: number, row: IIndustryPublisherRow) => {
                            const { changeState, precision } = getChangeCellValues({
                                value: visitsChange,
                                isNew: row.isVisitsNew,
                            });
                            return renderChangeCell(visitsChange, precision, changeState);
                        },
                    },
                ],
            },
        ] as ColumnsType<IIndustryPublisherRow>
    ).map((col) => findAndSetColumnSortOrder(col, sort.sort, sort.asc));
